<template>
  <div>
    <h1>Device Data Management</h1>
    <v-row class="mt-4">
      <v-col cols="12">
        <bolt-scene
          :rotation="rotation"
          :start-rotation="startRotation"
          :end-rotation="endRotation"
          :rotation-axis="rotationAxis"
          :auto-rotate="autoRotate"
          :rotation-speed="rotationSpeed"
          :is-animating="isAnimating"
          :thresholds="thresholds"
          :set-torque="setTorque"
          @animation-complete="isAnimating = false"
          @update:rotation="rotation = $event"
          class="three-container"
        />
      </v-col>
    </v-row>
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Device Information</v-card-title>
          <v-card-text>
            <label class="text-color-light mb-2">Device ID</label>
            <v-text-field
              v-model="deviceKey"
              outlined
              placeholder="1"
              hide-details
              class="
                font-size-input
                border border-radius-md
                placeholder-lighter
                text-color-light
                mb-4
              "
            ></v-text-field>
            <label class="text-color-light mb-2">Device Name</label>
            <v-text-field
              v-model="deviceId"
              outlined
              placeholder="ximu3-001"
              hide-details
              class="
                font-size-input
                border border-radius-md
                placeholder-lighter
                text-color-light
                mb-4
              "
            ></v-text-field>
            <label class="text-color-light mb-2">Device Location</label>
            <v-text-field
              v-model="deviceLocation"
              outlined
              placeholder="Pumping Station, Sector 5"
              hide-details
              class="
                font-size-input
                border border-radius-md
                placeholder-lighter
                text-color-light
                mb-4
              "
            ></v-text-field>
            <label class="text-color-light mb-2">Set Torque (Nm)</label>
            <v-text-field
              v-model.number="setTorque"
              type="number"
              outlined
              hide-details
              class="
                font-size-input
                border border-radius-md
                placeholder-lighter
                text-color-light
                mb-4
              "
            ></v-text-field>
            <label class="text-color-light mb-2">Location To Add</label>
            <v-select
              v-model="selectedLocationId"
              :items="locationItems"
              item-text="text"
              item-value="value"
              outlined
              hide-details
              class="
                font-size-input
                border border-radius-md
                placeholder-lighter
                text-color-light
                mb-4
              "
            ></v-select>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Rotation Controls</v-card-title>
          <v-card-text>
            <h3 class="text-subtitle-1 mb-2">Color Thresholds</h3>
            <v-row>
              <v-col cols="4">
                <label class="text-color-light mb-2">Secure Threshold</label>
                <v-text-field
                  v-model.number="thresholds.green"
                  type="number"
                  outlined
                  placeholder="5"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                  "
                ></v-text-field>
                <div class="text-caption text-color-light mt-1">
                  Degrees for green color
                </div>
              </v-col>
              <v-col cols="4">
                <label class="text-color-light mb-2">Warning Threshold</label>
                <v-text-field
                  v-model.number="thresholds.yellow"
                  type="number"
                  outlined
                  placeholder="15"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                  "
                ></v-text-field>
                <div class="text-caption text-color-light mt-1">
                  Degrees for yellow color
                </div>
              </v-col>
              <v-col cols="4">
                <label class="text-color-light mb-2">Critical Threshold</label>
                <v-text-field
                  v-model.number="thresholds.red"
                  type="number"
                  outlined
                  placeholder="45"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                  "
                ></v-text-field>
                <div class="text-caption text-color-light mt-1">
                  Degrees for red color
                </div>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <v-btn
              @click="saveDeviceData"
              color="primary"
              dark
              block
              class="mt-4"
              >Submit Device</v-btn
            >

            <v-card outlined class="mt-4 pa-4">
              <v-card-title class="pa-0 pb-2">Form Data Preview</v-card-title>
              <pre class="text-caption">{{
                JSON.stringify(formData, null, 2)
              }}</pre>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BoltScene from "@/components/BoltScene.vue";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import {
  postObject,
  updateSingleObjectToFirebase,
  fetchSingleObjectByKey,
  fetchObjects,
} from "@/services/firebase-helpers";

export default {
  name: "DataView",
  components: {
    BoltScene,
  },
  data() {
    return {
      deviceKey: "",
      deviceId: "",
      deviceLocation: "",
      setTorque: 150,
      rotation: 0,
      startRotation: 0,
      endRotation: 45,
      rotationAxis: "y",
      isAnimating: false,
      thresholds: {
        green: 5,
        yellow: 15,
        red: 45,
      },
      isSaving: false,
      selectedLocationId: null,
    };
  },
  computed: {
    locations() {
      return this.$store.state.locations || [];
    },
    locationItems() {
      return this.locations.map((location) => ({
        text: `${location.name} (${location.city}, ${location.state})`,
        value: location.key,
      }));
    },
    selectedLocation() {
      if (!this.selectedLocationId) return null;
      return this.locations.find((loc) => loc.key === this.selectedLocationId);
    },
    formData() {
      return {
        key: this.deviceKey,
        deviceId: this.deviceId,
        setTorque: this.setTorque,
        deviceLocation: this.deviceLocation,
        thresholds: this.thresholds,
        //locationId: this.selectedLocationId,
        //locationName: this.selectedLocation ? this.selectedLocation.name : null,
      };
    },
  },
  mounted() {
    // Set default values from placeholders
    this.deviceKey = "ximu3-001";
    this.deviceId = "ximu3-001";
    this.deviceLocation = "Pumping Station, Sector 5";

    // Fetch locations if they're not already loaded
    if (!this.locations.length) {
      this.$store.dispatch("handleUserLocations", this.$store.state.auth.uid);
    }
  },
  methods: {
    startAnimation() {
      this.isAnimating = true;
    },
    async saveDeviceData() {
      try {
        this.isSaving = true;

        // Create the device data object
        const deviceData = {
          deviceId: this.deviceId,
          location: this.deviceLocation,
          setTorque: this.setTorque,
          thresholds: this.thresholds,
          //locationId: this.selectedLocationId,
          // locationName: this.selectedLocation
          //   ? this.selectedLocation.name
          //   : null,
          lastUpdated: new Date().toISOString(),
        };

        // Try to post the device to Firebase using deviceKey as the index
        let devicePosted = false;
        try {
          const result = await postObject(deviceData, "devices", this.deviceKey);
          devicePosted = !!result;
        } catch (error) {
          console.log("Device may already exist, continuing to update location:", error);
        }

        // Proceed with updating the location regardless of device creation success
        if (this.selectedLocationId) {
          // Get all locations and filter to find the selected one
          const allLocations = await fetchObjects("location");
          const locationData = allLocations.find(location => location.key === this.selectedLocationId);

          if (locationData) {
            // Create or update the devices array
            const updatedDevices = locationData.devices || [];

            // Add the device ID if it's not already in the array
            if (!updatedDevices.includes(this.deviceKey)) {
              updatedDevices.push(this.deviceKey);
            }

            // Update the location with the new devices array
            await updateSingleObjectToFirebase(
              { ...locationData, devices: updatedDevices },
              "location",
              this.selectedLocationId
            );
            
            // Show success message based on what happened
            if (devicePosted) {
              alert("Device data saved successfully and added to location!");
            } else {
              alert("Device added to location! (Device already existed)");
            }
          } else {
            alert("Location not found! Please check the selected location.");
          }
        } else {
          if (devicePosted) {
            alert("Device data saved successfully!");
          } else {
            alert("Could not create device - it may already exist.");
          }
        }
      } catch (error) {
        console.error("Error in save operation:", error);
        alert("Error: " + error.message);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.three-container {
  width: 100%;
  height: 500px;
  background-color: #f0f0f0;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: block;
}
</style>
